import { isEmpty, isPresent } from '@ember/utils';
import currency from 'currency.js';
import dateUtils from 'donor-elf-web/lib/date-utils';
import JournalReportDefault from 'donor-elf-web/lib/models/journal-report-default';
import type JournalReportModel from 'donor-elf-web/models/all-funds/journal-report';
import JournalGoalModel from 'donor-elf-web/models/journal-goal';
import PledgeModel from 'donor-elf-web/models/pledge';
import FormatDateService from 'donor-elf-web/services/format-date-service';
import moment from 'moment';
import { pledgeOneTimeFrequency } from '../pledge-utils';
import JournalContactInfo from './journal-contact-info';
import JournalDecisionInfo from './journal-decision-info';
import JournalInfo from './journal-info';

// todo: move this to JournalReportDefault when that is converted to typescript
// ****
interface DateInfo {
  start: string;
  end: string;
}
// ****

interface CompletedTaskCounts {
  engagement: number;
  engagementTaskIds: number[];
  engagementCumulative: number;
  ask: number;
  askTaskIds: number[];
  askCumulative: number;
  recommendationAsk: number;
  recommendationAskTaskIds: number[];
  recommendationAskCumulative: number;
  followup: number;
  followupTaskIds: number[];
  followupCumulative: number;
}

interface OpenAsk {
  contactId: string;
}

interface IntervalItem {
  date: string;
  dateFormatted: string;
  dateRangeFormatted: string;
  completedTaskCounts: CompletedTaskCounts;
  openAsks: OpenAsk[];
  decisionInfos: JournalDecisionInfo[];
  decisionCumulativeCount: number;
  firstTimeDecisionCount: number; //number of first time decsisions by contact (excludes multiple decisions from contact)
  goalsData: any;
  points: JournalReportIntervarsityPoints;
}

interface AskStatus {
  closed: number;
  open: number;
  closedAndOpen: number;
  pending: number;
  none: number;
  pendingAndNone: number;
}

interface DecisionTypeAmounts {
  recurring: number;
  recurringPercent: number;
  oneTime: number;
  oneTimePercent: number;
}

interface AskStage {
  contact_id: number;
  pending?: string;
  open?: string;
  decision?: string;
}

interface TotalRecommendations {
  asksMade: number;
  recommendationsCollected: number;
  totalDecisionCountFromRecommendations: number;
  totalDecisionAmountFromRecommendations: number;
}

interface DecisionItem {
  date: string;
  decisionInfo: JournalDecisionInfo;
}

interface JournalReportIntervarsityData {
  ask_stages: AskStage[];
  engagement_tasks: { [date: string]: number[] };
  ask_tasks: { [date: string]: number[] };
  recommendation_ask_tasks: { [date: string]: number[] };
  recommendations: { [date: string]: string[] };
  followup_tasks: { [date: string]: number[] };
  thank_tasks: { [date: string]: number[] };
  total_recommendations_collected: number;
}

export interface JournalReportIntervarsityPoints {
  engages: number;
  asks: number;
  recommendationAsks: number;
  recommendations: number;
  followUps: number;
  thankYous: number;
  total: number;
}

export default class JournalReportIntervarsity {
  constructor({
    journalInfo,
    journalReport,
    journalContactInfos,
    formatDateService,
  }: {
    journalInfo: JournalInfo;
    journalReport: JournalReportModel;
    journalContactInfos: JournalContactInfo[];
    formatDateService: FormatDateService;
  }) {
    let { intervalDates } = journalReport;
    let data = journalReport.data as JournalReportIntervarsityData;
    this.goals = journalInfo.goals;
    let decisionItems = this.decisionItems(journalContactInfos);
    let dataByIntervalDate: any = {};
    let contactIdHasDecision: any = {};
    let decisionTotalAmount = 0;
    let decisionCumulativeCount = 0;
    this.totalCompletedTaskEngagements = 0;
    this.totalCompletedTaskAsks = 0;
    let completedTaskRecommendationAskCumulativeCount = 0;
    let completedTaskFollowupCumulativeCount = 0;
    let cumulativePoints = 0;
    for (let dateInfo of intervalDates || []) {
      let date = dateInfo.start;
      let dateEnd = dateInfo.end;
      let dateRangeFormatted = JournalReportDefault.getDateRangeFormatted({
        formatDateService,
        ...dateInfo,
      });

      let item = {
        date,
        firstTimeDecisionCount: 0,
        decisionCumulativeCount: decisionCumulativeCount,
      } as IntervalItem;

      item.dateFormatted = dateRangeFormatted;

      let completedTaskEngagementCount =
        data.engagement_tasks[date]?.length || 0;
      this.totalCompletedTaskEngagements += completedTaskEngagementCount;
      let completedTaskAskCount = data.ask_tasks[date]?.length || 0;
      this.totalCompletedTaskAsks += completedTaskAskCount;
      let completedTaskRecommendationAskCount =
        data.recommendation_ask_tasks[date]?.length || 0;
      completedTaskRecommendationAskCumulativeCount +=
        completedTaskRecommendationAskCount;
      let completedTaskFollowupCount = data.followup_tasks[date]?.length || 0;
      completedTaskFollowupCumulativeCount += completedTaskFollowupCount;
      item.completedTaskCounts = {
        engagement: completedTaskEngagementCount,
        engagementTaskIds: data.engagement_tasks[date] || [],
        engagementCumulative: this.totalCompletedTaskEngagements,
        ask: completedTaskAskCount,
        askTaskIds: data.ask_tasks[date] || [],
        askCumulative: this.totalCompletedTaskAsks,
        recommendationAsk: completedTaskRecommendationAskCount,
        recommendationAskTaskIds: data.recommendation_ask_tasks[date] || [],
        recommendationAskCumulative:
          completedTaskRecommendationAskCumulativeCount,
        followup: completedTaskFollowupCount,
        followupTaskIds: data.followup_tasks[date] || [],
        followupCumulative: completedTaskFollowupCumulativeCount,
      };
      item.openAsks = data.ask_stages
        .filter(
          (x: any) =>
            isPresent(x.open) &&
            x.open <= dateEnd &&
            (isEmpty(x.decision) || x.decision > dateEnd),
        )
        .map((x: any) => {
          return { contactId: x.contact_id };
        });

      item.decisionInfos = [];
      for (let decisionItem of decisionItems) {
        if (decisionItem.date >= date && decisionItem.date <= dateEnd) {
          item.decisionInfos.push(decisionItem.decisionInfo);
          if (
            !contactIdHasDecision[
              decisionItem.decisionInfo.journalDecision.journalContactId
            ]
          ) {
            item.firstTimeDecisionCount += 1;
            item.decisionCumulativeCount += 1;
            decisionCumulativeCount += 1;
            contactIdHasDecision[
              decisionItem.decisionInfo.journalDecision.journalContactId
            ] = true;
          }
        }
      }
      item.goalsData = {};
      this.goals.forEach((goal: any) => {
        let decisionNewAmount = 0;
        let goalDecisionInfos = item.decisionInfos.filter(
          (x) => x.journalGoal?.id === goal.id,
        );
        goalDecisionInfos.forEach((decisionInfo) => {
          decisionNewAmount = currency(decisionNewAmount).add(
            decisionInfo.journalDecision.amount,
          ).value;
        });
        decisionTotalAmount =
          currency(decisionTotalAmount).add(decisionNewAmount).value;
        let decisionTotalPercent = Math.round(
          (decisionTotalAmount * 100) / goal.amount,
        );

        item.goalsData[goal.id] = {
          goalId: goal.id,
          decisions: goalDecisionInfos,
          decisionNewAmount,
          decisionTotalAmount,
          decisionTotalPercent,
        };
      });

      let points: JournalReportIntervarsityPoints = {
        engages: 0,
        asks: 0,
        recommendationAsks: 0,
        recommendations: 0,
        followUps: 0,
        thankYous: 0,
        total: 0,
      };
      points.engages = item.completedTaskCounts.engagement * 1;
      points.asks = item.completedTaskCounts.ask * 10;
      points.recommendationAsks =
        item.completedTaskCounts.recommendationAsk * 2;
      points.recommendations = (data.recommendations[date]?.length || 0) * 2;
      points.followUps = item.completedTaskCounts.followup * 2;
      points.thankYous = (data.thank_tasks[date]?.length || 0) * 2;
      points.total =
        points.engages +
        points.asks +
        points.recommendationAsks +
        points.recommendations +
        points.followUps +
        points.thankYous;
      cumulativePoints += points.total;

      item.points = points;

      dataByIntervalDate[date] = item;
    }

    this.intervalData = journalReport.intervalDates!.map(
      (x: any) => dataByIntervalDate[x.start],
    );

    this.currentAskStatus = {
      closed: 0,
      open: 0,
      closedAndOpen: 0,
      pending: 0,
      none: 0,
      pendingAndNone: 0,
    };
    data.ask_stages.forEach((x: any) => {
      if (x.decision) {
        this.currentAskStatus.closed += 1;
      } else if (x.open) {
        this.currentAskStatus.open += 1;
      } else if (x.pending) {
        this.currentAskStatus.pending += 1;
      }
    });
    this.currentAskStatus.closedAndOpen =
      this.currentAskStatus.closed + this.currentAskStatus.open;
    this.currentAskStatus.none = journalContactInfos.filter(
      (x) =>
        !x.journalContact.finished &&
        !(x.decisionMade && x.journalDecisions[0].yesNo) &&
        x.tasksByStageId[x.journalInfo.askStage.id].length === 0,
    ).length;
    this.currentAskStatus.pendingAndNone =
      this.currentAskStatus.pending + this.currentAskStatus.none;

    let contactIdsInThisJournal: { [contactId: string]: boolean } = {};
    for (let journalContactInfo of journalContactInfos) {
      contactIdsInThisJournal[journalContactInfo.contactId!] = true;
    }
    let recommendedJournalContactIds: { [journalContactId: string]: boolean } =
      {};
    for (let journalContactInfo of journalContactInfos) {
      if (
        journalContactInfo.recommendedByContactIds?.some(
          (contactId) => contactIdsInThisJournal[contactId],
        )
      ) {
        recommendedJournalContactIds[journalContactInfo.journalContact.id] =
          true;
      }
    }
    let totalDecisionAmountFromRecommendations = 0;
    let totalDecisionCountFromRecommendations = 0;
    this.decisionTypeAmounts = {
      recurring: 0,
      recurringPercent: 0,
      oneTime: 0,
      oneTimePercent: 0,
    };

    for (let item of decisionItems) {
      if (item.decisionInfo.journalDecision.yesNo) {
        if (decisionTypeIsRecurring(item.decisionInfo.pledge!)) {
          this.decisionTypeAmounts.recurring = currency(
            this.decisionTypeAmounts.recurring,
          ).add(item.decisionInfo.journalDecision.amount).value;
        } else {
          this.decisionTypeAmounts.oneTime = currency(
            this.decisionTypeAmounts.oneTime,
          ).add(item.decisionInfo.journalDecision.amount).value;
        }
        if (
          recommendedJournalContactIds[
            item.decisionInfo.journalDecision.journalContactId
          ]
        ) {
          totalDecisionCountFromRecommendations += 1;
          totalDecisionAmountFromRecommendations = currency(
            totalDecisionAmountFromRecommendations,
          ).add(item.decisionInfo.journalDecision.amount).value;
        }
      }
    }
    let totalDecisionAmount =
      this.decisionTypeAmounts.recurring + this.decisionTypeAmounts.oneTime;
    if (totalDecisionAmount > 0) {
      let goalAmount = this.goals[0]?.amount || 1;
      this.decisionTypeAmounts.recurringPercent = Math.round(
        (this.decisionTypeAmounts.recurring * 100) / goalAmount,
      );
      this.decisionTypeAmounts.oneTimePercent = Math.round(
        (this.decisionTypeAmounts.oneTime * 100) / goalAmount,
      );
    }

    this.totalRecommendations = {
      asksMade: completedTaskRecommendationAskCumulativeCount,
      recommendationsCollected: data.total_recommendations_collected,
      totalDecisionCountFromRecommendations,
      totalDecisionAmountFromRecommendations,
    };

    this.cumulativePoints = cumulativePoints;
  }

  /*  [{
        date: '2020-10-29',
        dateFormatted: '10/29/21',
        dateRangeFormatted: '10/29 - 11/03',
        completedTaskCounts: {
          engagement: 1,
          engagementCumulative: 2,
          ask: 1,
          askCumulative: 2
        }
        openAsks: [{ contactId: 123 }],
        decisionInfos: [JournalDecisionInfo],
        decisionCumulativeCount: 1,
        firstTimeDecisionCount: 1, number of first time decsisions by contact (excludes multiple decisions from contact)
        goalsData: {
          goalId: {
            goalId: string,
            decisions: [JournalDecisionInfo],
            decisionNewAmount: number,
            decisionTotalAmount: number
            decisionTotalPercent: number
          }
        },
        points: JournalReportIntervarsityPoints
      }]
  */
  intervalData: IntervalItem[];

  currentAskStatus: AskStatus;

  // [{ JournalGoalModel }]
  goals: JournalGoalModel[];

  decisionTypeAmounts: DecisionTypeAmounts;

  totalRecommendations: TotalRecommendations;

  totalCompletedTaskEngagements: number;
  totalCompletedTaskAsks: number;
  cumulativePoints: number;

  // [{ date: '2021-03-03', decisionInfo: JournalDecisionInfo }]
  decisionItems(journalContactInfos: JournalContactInfo[]): DecisionItem[] {
    let data: DecisionItem[] = [];
    journalContactInfos.forEach((info) => {
      info.journalDecisionInfos.forEach((decisionInfo) => {
        data.push({
          date: dateUtils.serializeToDateOnly(
            decisionInfo.journalDecision.decisionDate,
          ),
          decisionInfo,
        });
      });
    });
    return data;
  }
}

function decisionTypeIsRecurring(pledge: PledgeModel) {
  if (!pledge) {
    // temporary fix for some pledges changing contact_id in InterVaristy
    return false;
  }
  if (pledge.frequency === pledgeOneTimeFrequency) {
    return false;
  }
  if (pledge.endDate) {
    return moment(pledge.startDate).add('year', 1).toDate() <= pledge.endDate;
  } else {
    return true;
  }
}
